// src/components/admin/BookingCalendarTab.jsx - Part 1 of 3: Imports, State, and Helper Functions
import React, { useState, useEffect } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  query,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { notifyCustomerOfAppointmentStatus } from '../../utils/bookingNotifications';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import { canShareSlot } from '../../utils/multiServiceAvailability';
import '../../styles/booking-calendar.css';
import '../../styles/responsive-utils.css';
import '../../styles/booking-calendar-responsive.css';

function BookingCalendarTab({ salonId, onPreview, locationId, onLocationChange }) {
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [view, setView] = useState('consolidated');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [services, setServices] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [businessHoursData, setBusinessHoursData] = useState({
    regularHours: [],
    holidayClosures: [],
    customClosures: []
  });
  const [rawBusinessHoursFromDB, setRawBusinessHoursFromDB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showCancelReasonModal, setShowCancelReasonModal] = useState(false);
  const [cancelReasonTarget, setCancelReasonTarget] = useState(null);
  const [cancelReasonText, setCancelReasonText] = useState('');
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedTimeSlotData, setSelectedTimeSlotData] = useState(null);

  const [activeRequestsTab, setActiveRequestsTab] = useState('pending');
  const [requestsTimeFilter, setRequestsTimeFilter] = useState('week');
  const [requestsSearchTerm, setRequestsSearchTerm] = useState('');
  const [showRequestsSection, setShowRequestsSection] = useState(true);
  const [summaryTimeFilter, setSummaryTimeFilter] = useState('week');
  const [workingHoursData, setWorkingHoursData] = useState({});

  const getWeekDates = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentDate);

  // Derive calendar start/end from the current location's business hours so each
  // location's calendar only shows its actual operating window.
  const calendarRange = (() => {
    const openDays = businessHoursData.regularHours.filter(
      d => !d.isClosed && d.open && d.open !== '00:00'
    );
    if (openDays.length === 0) return { start: 8, end: 22 };
    const toHrs = (t) => { const [h, m] = t.split(':').map(Number); return h + m / 60; };
    const opens = openDays.map(d => toHrs(d.open));
    const closes = openDays.map(d => d.close === '00:00' ? 24 : toHrs(d.close));
    // Floor start to nearest half-hour, ceil end to nearest half-hour
    const start = Math.max(0, Math.floor(Math.min(...opens) * 2) / 2);
    const end = Math.min(24, Math.ceil(Math.max(...closes) * 2) / 2);
    return { start, end };
  })();
  const timeSlots = Array.from(
    { length: Math.round((calendarRange.end - calendarRange.start) * 2) },
    (_, i) => calendarRange.start + (i * 0.5)
  );
  const SLOT_HEIGHT = 50;

  // Client-side location filter — applies when a specific location is selected
  const visibleAppointments = (locationId && appointments.length > 0)
    ? appointments.filter(a => !a.locationId || a.locationId === locationId)
    : appointments;

  const visibleStaffs = (locationId && staffs.length > 0)
    ? staffs.filter(s =>
        (s.locationIds && s.locationIds.includes(locationId)) ||
        s.locationId === locationId ||
        (!s.locationIds && !s.locationId)
      )
    : staffs;

  // Lookup maps for the multi-service slot-sharing rule
  const servicesById = {};
  services.forEach(s => { servicesById[s.id] = s; });
  const staffById = {};
  visibleStaffs.forEach(s => { staffById[s.id] = s; });

  const getFilteredAppointments = (status) => {
    if (!Array.isArray(visibleAppointments)) return [];

    let filtered = visibleAppointments.filter(apt => apt.status === status);
    
    const now = new Date();
    const timeFilterDate = new Date();
    
    switch (requestsTimeFilter) {
      case 'week':
        timeFilterDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        timeFilterDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        timeFilterDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        timeFilterDate.setDate(now.getDate() - 7);
    }
    
    filtered = filtered.filter(appointment => {
      const appointmentDate = appointment.dateTimeCreated || appointment.startDateTime;
      if (!appointmentDate) {
        console.warn('Appointment missing both dateTimeCreated and startDateTime:', appointment);
        return false;
      }
      return appointmentDate >= timeFilterDate;
    });
    
    if (requestsSearchTerm.trim()) {
      const searchLower = requestsSearchTerm.toLowerCase();
      filtered = filtered.filter(appointment => {
        const customerName = (appointment.customerName || '').toLowerCase();
        const customerEmail = (appointment.customerEmail || '').toLowerCase();
        const serviceName = (appointment.serviceName || '').toLowerCase();
        const staffName = (appointment.staffName || '').toLowerCase();
        
        return customerName.includes(searchLower) || 
               customerEmail.includes(searchLower) ||
               serviceName.includes(searchLower) ||
               staffName.includes(searchLower);
      });
    }
    
    return filtered.sort((a, b) => {
      const dateA = a.dateTimeCreated || a.startDateTime;
      const dateB = b.dateTimeCreated || b.startDateTime;
      return new Date(dateB) - new Date(dateA);
    });
  };

  const getPendingCount = () => {
    if (!Array.isArray(visibleAppointments)) return 0;
    return visibleAppointments.filter(apt => apt.status === 'pending').length;
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        console.log('Loading staff from main staff collection');
        const staffsQuery = query(
          collection(db, 'staff'),
          where('salonId', '==', salonId)
        );
        const staffsSnapshot = await getDocs(staffsQuery);
        const staffsData = staffsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded staffs:', staffsData);
        setStaffs(staffsData);

        try {
          const servicesQuery = collection(db, 'services');
          const servicesSnapshot = await getDocs(servicesQuery);
          const servicesData = servicesSnapshot.docs
            .filter(doc => doc.data().salonId === salonId)
            .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setServices(servicesData);
        } catch (error) {
          console.log('Services collection not found, continuing without it');
          setServices([]);
        }

        // Working hours are loaded separately in the locationId-aware effect below

        const salonDocRef = doc(db, 'salons', salonId);
        const unsubscribeSalon = onSnapshot(salonDocRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            console.log('Salon config data loaded:', data);
            
            setRawBusinessHoursFromDB(data.businessHours || null);
          }
        });
        return () => unsubscribeSalon();
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [salonId]);

  // When the selected location changes, clear any staff that was selected in
  // Individual Staff View so we don't keep showing a staff member from the old location.
  useEffect(() => {
    setSelectedStaff(null);
  }, [locationId]);

  // Derive businessHoursData from raw Firestore data, picking the correct location.
  useEffect(() => {
    const raw = rawBusinessHoursFromDB;
    if (!raw) {
      setBusinessHoursData({ regularHours: [], holidayClosures: [], customClosures: [] });
      return;
    }

    let regularHours, customClosures, holidayClosures;

    if (Array.isArray(raw)) {
      // Very old format: businessHours stored as a plain array
      regularHours = raw;
      customClosures = [];
      holidayClosures = [];
    } else {
      holidayClosures = raw.holidayClosures || [];
      if (locationId && raw.locations && raw.locations[locationId]) {
        // Use this specific location's hours
        regularHours = raw.locations[locationId].regularHours || [];
        customClosures = raw.locations[locationId].customClosures || [];
      } else {
        // Fall back to top-level (legacy or first-location copy written by BusinessHoursEditor)
        regularHours = raw.regularHours || [];
        customClosures = raw.customClosures || [];
      }
    }

    setBusinessHoursData({ regularHours, holidayClosures, customClosures });
  }, [rawBusinessHoursFromDB, locationId]);

  // Reload working hours whenever the selected location changes.
  // For multi-location salons: query by salonId + locationId (part-time staff only show
  // hours for the selected location). Falls back to any doc for the staff if no
  // location-specific record exists (covers single-location and head-office defaults).
  useEffect(() => {
    if (!salonId) return;

    const loadWorkingHours = async () => {
      try {
        let whSnapshot;

        if (locationId) {
          // Try location-specific records first
          const whQuery = query(
            collection(db, 'workingHours'),
            where('salonId', '==', salonId),
            where('locationId', '==', locationId)
          );
          whSnapshot = await getDocs(whQuery);

          // If nothing found for this location, fall back to all records for the salon
          // (covers single-location salons and head-office defaults before location hours are defined)
          if (whSnapshot.empty) {
            const fallbackQuery = query(
              collection(db, 'workingHours'),
              where('salonId', '==', salonId)
            );
            whSnapshot = await getDocs(fallbackQuery);
          }
        } else {
          const whQuery = query(
            collection(db, 'workingHours'),
            where('salonId', '==', salonId)
          );
          whSnapshot = await getDocs(whQuery);
        }

        const whMap = {};
        whSnapshot.docs.forEach(d => {
          const data = d.data();
          if (data.staffId) {
            // Only overwrite if we haven't already stored a location-specific record
            // (prevents a fallback doc from overwriting a location-specific one)
            if (!whMap[data.staffId] || data.locationId === locationId) {
              whMap[data.staffId] = {
                employmentType: data.employmentType || 'fulltime',
                roster: data.roster || {},
                offDay: data.offDay || null
              };
            }
          }
        });

        console.log('Loaded working hours data for location', locationId, ':', whMap);
        setWorkingHoursData(whMap);
      } catch (error) {
        console.log('Working hours collection not found, continuing without it');
        setWorkingHoursData({});
      }
    };

    loadWorkingHours();
  }, [salonId, locationId]);

  useEffect(() => {
    if (!salonId) return;

    console.log('Setting up real-time listener for appointments');
    
    const appointmentsRef = collection(db, 'appointments');
    const appointmentsQuery = query(appointmentsRef, where('salonId', '==', salonId));
    
    const unsubscribeAppointments = onSnapshot(appointmentsQuery, (appointmentsSnapshot) => {
      const allAppointments = [];
      
      appointmentsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('Appointment found:', doc.id, data);
        
        if (data.salonId === salonId) {
          const startDateTime = data.dateTimeRequested?.toDate ? 
            data.dateTimeRequested.toDate() : 
            data.startDateTime?.toDate ? 
            data.startDateTime.toDate() : 
            new Date(data.dateTimeRequested || data.startDateTime);
          
          let endDateTime;
          if (startDateTime && data.serviceRequested?.duration) {
            endDateTime = new Date(startDateTime);
            endDateTime.setMinutes(endDateTime.getMinutes() + data.serviceRequested.duration);
          } else if (data.endDateTime?.toDate) {
            endDateTime = data.endDateTime.toDate();
          } else if (data.endDateTime) {
            endDateTime = new Date(data.endDateTime);
          } else {
            endDateTime = new Date(startDateTime);
            endDateTime.setMinutes(endDateTime.getMinutes() + 60);
          }
          
          const dateTimeCreated = data.dateTimeCreated?.toDate ? 
            data.dateTimeCreated.toDate() : 
            new Date(data.dateTimeCreated);
          
          const updatedAt = data.updatedAt?.toDate ? 
            data.updatedAt.toDate() : 
            new Date(data.updatedAt);
          
          let serviceName = data.serviceRequested?.name || 'Unknown Service';
          if (data.serviceId && services.length > 0) {
            const service = services.find(s => s.id === data.serviceId);
            if (service) {
              serviceName = service.name;
            }
          }
          
          allAppointments.push({
            id: doc.id,
            ...data,
            startDateTime: startDateTime,
            endDateTime: endDateTime,
            dateTimeCreated: dateTimeCreated,
            updatedAt: updatedAt,
            serviceName: serviceName,
            duration: data.serviceRequested?.duration || 60,
            price: data.serviceRequested?.price || 0
          });
        }
      });
      
      console.log('All appointments loaded:', allAppointments);
      console.log('Approved appointments:', allAppointments.filter(apt => apt.status === 'approved'));
      setAppointments(allAppointments);
    });

    return () => unsubscribeAppointments();
  }, [salonId, services]);

  useEffect(() => {
    if (!salonId) return;

    console.log('Setting up real-time listener for leave data');
    
    const leaveRef = collection(db, 'leave');
    const leaveQuery = query(leaveRef, where('salonId', '==', salonId));
    
    const unsubscribeLeave = onSnapshot(leaveQuery, (leaveSnapshot) => {
      const allLeaveData = [];
      
      leaveSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('Leave record found:', doc.id, data);
        
        // Only include approved leave in the calendar
        if (data.salonId === salonId && data.startDate && data.endDate && (data.status === 'approved' || !data.status)) {
          const startDate = data.startDate.toDate ? data.startDate.toDate() : new Date(data.startDate);
          const endDate = data.endDate.toDate ? data.endDate.toDate() : new Date(data.endDate);
          const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          
          const staff = staffs.find(s => s.id === data.staffId);
          const staffName = staff ? staff.name : 'Unknown Staff';
          
          allLeaveData.push({
            id: doc.id,
            ...data,
            startDate: startDate,
            endDate: endDate,
            createdAt: createdAt,
            staffName: staffName
          });
        }
      });
      
      console.log('All leave data loaded:', allLeaveData);
      setLeaveData(allLeaveData);
    });

    return () => unsubscribeLeave();
  }, [salonId, staffs]);

  const handleAppointmentStatusChange = async (appointmentId, newStatus) => {
    try {
      console.log('Updating appointment status:', appointmentId, 'to:', newStatus);

      // Fetch the appointment data before updating so we can use it for the notification
      const appointmentRef = doc(db, 'appointments', appointmentId);
      const appointmentSnap = await getDoc(appointmentRef);
      const appointmentData = appointmentSnap.exists() ? appointmentSnap.data() : null;

      await updateDoc(appointmentRef, {
        status: newStatus,
        updatedAt: new Date(),
        ...(newStatus === 'approved' && { approvedAt: new Date() })
      });

      // Send push notification and email to customer on approval or rejection (non-blocking)
      if (appointmentData && (newStatus === 'approved' || newStatus === 'rejected')) {
        notifyCustomerOfAppointmentStatus({
          salonId,
          appointmentId,
          status: newStatus,
          customerEmail: appointmentData.customerEmail,
          customerName: appointmentData.customerName,
          serviceName: appointmentData.serviceRequested?.name || appointmentData.serviceName,
          staffName: appointmentData.staffName,
          appointmentDateTime: appointmentData.dateTimeRequested?.toDate
            ? appointmentData.dateTimeRequested.toDate()
            : new Date(appointmentData.dateTimeRequested)
        });
      }

      setShowRequestModal(false);
      setSelectedRequest(null);
      setShowAppointmentModal(false);
      setSelectedAppointment(null);

      console.log('Appointment status updated successfully');

    } catch (error) {
      console.error('Error updating appointment status:', error);
      alert('Failed to update appointment status. Please try again.');
    }
  };

  // Cancelling an approved booking requires a reason (same as Booking Requests). The reason is stored with the
  // cancellation so the customer sees it in their portal alert and My Appointments, and the customer also gets the
  // WhatsApp booking_cancelled notice (when enabled for the salon and they have opted in).
  const openCancelReason = (appointment) => {
    setCancelReasonTarget(appointment);
    setCancelReasonText('');
    setShowCancelReasonModal(true);
  };

  const handleCancelWithReason = async () => {
    if (!cancelReasonText.trim()) {
      alert('Please provide a reason for cancellation.');
      return;
    }
    try {
      await updateDoc(doc(db, 'appointments', cancelReasonTarget.id), {
        status: 'cancelled',
        cancelledAt: new Date(),
        updatedAt: new Date(),
        cancelledByAdmin: true,
        cancellationReason: cancelReasonText.trim()
      });
      notifyCustomerOfAppointmentStatus({ salonId, status: 'cancelled', appointmentId: cancelReasonTarget.id });
      setShowCancelReasonModal(false);
      setCancelReasonTarget(null);
      setCancelReasonText('');
      setShowRequestModal(false);
      setSelectedRequest(null);
      setShowAppointmentModal(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert('Failed to cancel appointment. Please try again.');
    }
  };

  const convertTimeToDecimal = (timeString) => {
    if (!timeString || timeString === '00:00') return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return 0;
    return hours + (minutes / 60);
  };

  const getSummaryDateRange = () => {
    const now = new Date();
    const startDate = new Date();
    
    switch (summaryTimeFilter) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
    }
    
    return { startDate, endDate: now };
  };

  const getFilteredSummaryCount = (status) => {
    const { startDate, endDate } = getSummaryDateRange();
    
    if (!Array.isArray(visibleAppointments)) return 0;

    const filteredByDate = visibleAppointments.filter(appointment => {
      const appointmentDate = appointment.dateTimeCreated || appointment.startDateTime;
      if (!appointmentDate) return false;
      
      const date = appointmentDate.toDate ? appointmentDate.toDate() : new Date(appointmentDate);
      return date >= startDate && date <= endDate;
    });
    
    if (status === 'total') {
      return filteredByDate.length;
    } else if (status === 'cancelled') {
      return filteredByDate.filter(apt => apt.status === 'cancelled' || apt.status === 'rejected').length;
    } else {
      return filteredByDate.filter(apt => apt.status === status).length;
    }
  };

  const getBusinessHoursForDay = (date) => {
    if (!Array.isArray(businessHoursData.regularHours) || businessHoursData.regularHours.length === 0) {
      return null;
    }

    const dayName = formatDayName(date);
    const businessDay = businessHoursData.regularHours.find(bh => bh && bh.day === dayName);
    
    console.log('Checking business hours for:', dayName, 'Found:', businessDay);
    
    if (!businessDay || businessDay.isClosed || !businessDay.open) {
      return null;
    }
    
    let closeTime;
    if (businessDay.close === '00:00' && businessDay.open !== '00:00') {
      closeTime = 24;
    } else {
      closeTime = convertTimeToDecimal(businessDay.close);
    }
    
    const openTime = convertTimeToDecimal(businessDay.open);
    
    if (isNaN(openTime) || isNaN(closeTime)) {
      return null;
    }
    
    return { openTime, closeTime, businessDay };
  };

  const isClosedForBusinessHours = (date, timeSlot) => {
    const businessHours = getBusinessHoursForDay(date);
    
    if (!businessHours) {
      return { isClosed: true, reason: 'Salon closed on ' + formatDayName(date) };
    }
    
    const { openTime, closeTime, businessDay } = businessHours;
    
    if (timeSlot < openTime || timeSlot >= closeTime) {
      return { 
        isClosed: true, 
        reason: `Outside business hours (${formatTime12Hour(businessDay.open)} - ${formatTime12Hour(businessDay.close)})` 
      };
    }
    
    return { isClosed: false, reason: '' };
  };

  const isClosedForHolidays = (date) => {
    if (!Array.isArray(businessHoursData.holidayClosures)) {
      return { isClosed: false, reason: '' };
    }

    const dateStr = formatDate(date);
    const holiday = businessHoursData.holidayClosures.find(closure => 
      closure && closure.date === dateStr
    );
    
    if (holiday) {
      return { 
        isClosed: true, 
        reason: `Holiday: ${holiday.name || 'Public Holiday'}` 
      };
    }
    
    return { isClosed: false, reason: '' };
  };

  const isClosedForCustomClosures = (date, timeSlot) => {
    if (!Array.isArray(businessHoursData.customClosures)) {
      return { isClosed: false, reason: '' };
    }

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    for (const closure of businessHoursData.customClosures) {
      if (!closure || !closure.startDate || !closure.endDate) continue;
      
      const startDate = new Date(closure.startDate);
      const endDate = new Date(closure.endDate);
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      if (checkDate >= startDate && checkDate <= endDate) {
        if (closure.isFullDay) {
          return { 
            isClosed: true, 
            reason: `Custom closure: ${closure.reason || 'Special closure'}` 
          };
        } else {
          if (closure.startTime && closure.endTime) {
            const startTime = convertTimeToDecimal(closure.startTime);
            const endTime = convertTimeToDecimal(closure.endTime);
            
            if (timeSlot >= startTime && timeSlot < endTime) {
              return { 
                isClosed: true, 
                reason: `Custom closure: ${closure.reason || 'Special closure'} (${formatTime12Hour(closure.startTime)} - ${formatTime12Hour(closure.endTime)})` 
              };
            }
          }
        }
      }
    }
    
    return { isClosed: false, reason: '' };
  };

  const isStaffOnLeave = (staffId, date, timeSlot) => {
    if (!staffId || !Array.isArray(leaveData)) {
      return { isOnLeave: false, reason: '', staffName: '', leaveRecord: null };
    }

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    const staffLeaveRecords = leaveData.filter(leave => leave.staffId === staffId);
    const staffName = staffs.find(s => s.id === staffId)?.name || 'Unknown';
    
    for (const leave of staffLeaveRecords) {
      if (!leave.startDate || !leave.endDate) {
        continue;
      }
      
      const leaveStart = new Date(leave.startDate);
      const leaveEnd = new Date(leave.endDate);
      
      leaveStart.setHours(0, 0, 0, 0);
      leaveEnd.setHours(23, 59, 59, 999);
      
      if (checkDate >= leaveStart && checkDate <= leaveEnd) {
        const leaveTypeLabels = {
          'annual': 'Annual Leave',
          'medical': 'Medical Leave',
          'timeoff': 'Time Off'
        };
        
        const leaveTypeLabel = leaveTypeLabels[leave.type] || 'Leave';
        
        if (leave.timeType === 'full') {
          const businessHours = getBusinessHoursForDay(date);
          if (businessHours) {
            const { openTime, closeTime } = businessHours;
            if (timeSlot >= openTime && timeSlot < closeTime) {
              return { 
                isOnLeave: true, 
                reason: `${leaveTypeLabel}: ${leave.reason || 'Not specified'}`,
                staffName: staffName,
                leaveRecord: leave
              };
            }
          } else {
            if (timeSlot >= 8 && timeSlot < 18) {
              return { 
                isOnLeave: true, 
                reason: `${leaveTypeLabel}: ${leave.reason || 'Not specified'}`,
                staffName: staffName,
                leaveRecord: leave
              };
            }
          }
        } else if (leave.timeType === 'am' && timeSlot >= 9 && timeSlot < 13) {
          return { 
            isOnLeave: true, 
            reason: `${leaveTypeLabel}: ${leave.reason || 'Not specified'}`,
            staffName: staffName,
            leaveRecord: leave
          };
        } else if (leave.timeType === 'pm' && timeSlot >= 13 && timeSlot < 18) {
          return { 
            isOnLeave: true, 
            reason: `${leaveTypeLabel}: ${leave.reason || 'Not specified'}`,
            staffName: staffName,
            leaveRecord: leave
          };
        } else if (leave.timeType === 'custom' && leave.startTime && leave.endTime) {
          const leaveStartTime = convertTimeToDecimal(leave.startTime);
          const leaveEndTime = convertTimeToDecimal(leave.endTime);
          
          if (timeSlot >= leaveStartTime && timeSlot < leaveEndTime) {
            return { 
              isOnLeave: true, 
              reason: `${leaveTypeLabel}: ${leave.reason || 'Not specified'}`,
              staffName: staffName,
              leaveRecord: leave
            };
          }
        }
      }
    }
    
    return { isOnLeave: false, reason: '', staffName: '', leaveRecord: null };
  };

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const isOutsideStaffWorkingHours = (staffId, date, timeSlot) => {
    const staffWH = workingHoursData[staffId];

    // Full-time staff: only block their configured off day(s)
    if (!staffWH || staffWH.employmentType === 'fulltime') {
      if (staffWH?.offDay) {
        const offDays = Array.isArray(staffWH.offDay) ? staffWH.offDay : [staffWH.offDay];
        if (offDays.includes(DAY_NAMES[date.getDay()])) {
          return { isOutside: true, isOffDay: true };
        }
      }
      return { isOutside: false };
    }

    // Part-time staff - check roster
    const roster = staffWH.roster || {};
    // Format date as YYYY-MM-DD using local date (not toISOString which uses UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;

    const daySlots = roster[dateKey];
    if (!daySlots || !Array.isArray(daySlots) || daySlots.length === 0) {
      return { isOutside: true, reason: 'Outside staff working hours' };
    }

    // Convert timeSlot (decimal) to HH:MM string
    const hour = Math.floor(timeSlot);
    const minutes = Math.round((timeSlot % 1) * 60);
    const timeString = `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    if (!daySlots.includes(timeString)) {
      return { isOutside: true, reason: 'Outside staff working hours' };
    }

    return { isOutside: false };
  };

  const getStaffAvailabilityData = (date, timeSlot) => {
    if (view !== 'consolidated') return null;

    const slotStart = new Date(date);
    const hour = Math.floor(timeSlot);
    const minutes = (timeSlot % 1) * 60;
    slotStart.setHours(hour, minutes, 0, 0);
    
    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);

    const activeStaffs = visibleStaffs.filter(staff => staff.isActive);

    if (activeStaffs.length === 0) {
      return { totalStaffs: 0, availableStaffs: [], occupiedStaffs: [] };
    }

    const availableStaffs = [];
    const occupiedStaffs = [];
    // Every staff with an overlapping approved booking, whether the slot ends up
    // "available" (shareable) or "occupied" — used to always surface the booking itself.
    const currentBookings = [];
    // Staff outside working hours or on leave are excluded from counts entirely
    let excludedCount = 0;

    for (const staff of activeStaffs) {
      // Check working hours / off day first — excluded from count entirely
      const whCheck = isOutsideStaffWorkingHours(staff.id, date, timeSlot);
      if (whCheck.isOutside) {
        excludedCount++;
        continue;
      }

      // Staff on leave are also excluded from the count
      const leaveCheck = isStaffOnLeave(staff.id, date, timeSlot);
      if (leaveCheck.isOnLeave) {
        excludedCount++;
        continue;
      }

      const staffAppointments = visibleAppointments.filter(appointment => {
        if (appointment.staffId !== staff.id) return false;

        if (appointment.status !== 'approved') return false;

        const appointmentStart = new Date(appointment.startDateTime);
        const appointmentEnd = new Date(appointment.endDateTime);

        return (appointmentStart < slotEnd && appointmentEnd > slotStart);
      });

      if (staffAppointments.length > 0) {
        staffAppointments.forEach(appointment => {
          currentBookings.push({
            staffId: staff.id,
            staffName: staff.name,
            customerName: appointment.customerName,
            serviceName: appointment.serviceName,
            duration: appointment.duration
          });
        });

        const staffServices = staff.services || [];
        const serviceDetails = staffServices.map(serviceId => {
          const service = services.find(s => s.id === serviceId || s.serviceId === serviceId);
          return service ? { id: service.id, name: service.name } : { id: serviceId, name: serviceId };
        });

        const overlappingServices = staffAppointments
          .map(appointment => servicesById[appointment.serviceId])
          .filter(Boolean);
        const shareable = canShareSlot(staff, overlappingServices);

        if (shareable) {
          availableStaffs.push({
            id: staff.id,
            name: staff.name,
            services: serviceDetails,
            sharedBookings: staffAppointments.map(appointment => ({
              customerName: appointment.customerName,
              serviceName: appointment.serviceName,
              duration: appointment.duration
            }))
          });
        } else {
          occupiedStaffs.push({
            id: staff.id,
            name: staff.name,
            type: 'booked',
            bookings: staffAppointments.map(appointment => ({
              customerName: appointment.customerName,
              serviceName: appointment.serviceName,
              duration: appointment.duration
            })),
            services: serviceDetails
          });
        }
      } else {
        const staffServices = staff.services || [];
        const serviceDetails = staffServices.map(serviceId => {
          const service = services.find(s => s.id === serviceId || s.serviceId === serviceId);
          return service ? { id: service.id, name: service.name } : { id: serviceId, name: serviceId };
        });

        availableStaffs.push({
          id: staff.id,
          name: staff.name,
          services: serviceDetails
        });
      }
    }

    // totalStaffs only counts staff who are working this slot (excludes outside hours + on leave)
    const totalStaffs = activeStaffs.length - excludedCount;

    return {
      totalStaffs,
      availableStaffs,
      occupiedStaffs,
      currentBookings,
      occupiedCount: occupiedStaffs.length,
      availableCount: availableStaffs.length
    };
  };

  const checkTimeSlotAvailability = (date, timeSlot, staffId = null) => {
    const salonReasons = [];
    
    const businessHoursCheck = isClosedForBusinessHours(date, timeSlot);
    if (businessHoursCheck.isClosed) {
      salonReasons.push(businessHoursCheck.reason);
    }
    
    const holidayCheck = isClosedForHolidays(date);
    if (holidayCheck.isClosed) {
      salonReasons.push(holidayCheck.reason);
    }
    
    const customClosureCheck = isClosedForCustomClosures(date, timeSlot);
    if (customClosureCheck.isClosed) {
      salonReasons.push(customClosureCheck.reason);
    }
    
    const staffReasons = [];
    let outsideWorkingHours = false;
    let isOffDay = false;
    let isSharedSlot = false;

    if (salonReasons.length === 0) {
      if (staffId) {
        const leaveCheck = isStaffOnLeave(staffId, date, timeSlot);
        if (leaveCheck.isOnLeave) {
          staffReasons.push(leaveCheck.reason);
        }

        // Check working hours / off day after leave check, before appointment check
        if (staffReasons.length === 0) {
          const whCheck = isOutsideStaffWorkingHours(staffId, date, timeSlot);
          if (whCheck.isOutside) {
            outsideWorkingHours = true;
            if (whCheck.isOffDay) isOffDay = true;
          }
        }

        if (staffReasons.length === 0 && !outsideWorkingHours) {
          const slotStart = new Date(date);
          const hour = Math.floor(timeSlot);
          const minutes = (timeSlot % 1) * 60;
          slotStart.setHours(hour, minutes, 0, 0);

          const slotEnd = new Date(slotStart);
          slotEnd.setMinutes(slotEnd.getMinutes() + 30);

          const overlappingAppointments = visibleAppointments.filter(appointment => {
            if (appointment.staffId !== staffId || appointment.status !== 'approved') return false;

            const appointmentStart = new Date(appointment.startDateTime);
            const appointmentEnd = new Date(appointment.endDateTime);

            return (appointmentStart < slotEnd && appointmentEnd > slotStart);
          });

          if (overlappingAppointments.length > 0) {
            const overlappingServices = overlappingAppointments
              .map(appointment => servicesById[appointment.serviceId])
              .filter(Boolean);

            if (canShareSlot(staffById[staffId], overlappingServices)) {
              isSharedSlot = true;
            } else {
              staffReasons.push('Staff has approved appointment');
            }
          }
        }
      } else if (view === 'consolidated') {
        const availabilityData = getStaffAvailabilityData(date, timeSlot);
        if (availabilityData && availabilityData.availableCount === 0 && availabilityData.totalStaffs > 0) {
          staffReasons.push('All staffs unavailable');
        }
      }
    }

    return {
      isSalonClosed: salonReasons.length > 0,
      salonReasons: salonReasons,
      hasStaffIssues: staffReasons.length > 0,
      staffReasons: staffReasons,
      outsideWorkingHours: outsideWorkingHours,
      isOffDay: isOffDay,
      isSharedSlot: isSharedSlot,
      isAvailable: salonReasons.length === 0 && staffReasons.length === 0 && !outsideWorkingHours
    };
  };

  const getAppointmentsForSlot = (date, timeSlot) => {
    if (view !== 'individual') return [];

    const slotStart = new Date(date);
    const hour = Math.floor(timeSlot);
    const minutes = (timeSlot % 1) * 60;
    slotStart.setHours(hour, minutes, 0, 0);

    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);

    const slotAppointments = [];

    if (Array.isArray(visibleAppointments)) {
      const appointmentItems = visibleAppointments.filter(appointment => {
        if (!appointment || !appointment.startDateTime || !appointment.endDateTime) {
          return false;
        }

        // Only show appointments with status "pending" or "approved"
        if (appointment.status !== 'pending' && appointment.status !== 'approved') {
          return false;
        }

        if (selectedStaff && appointment.staffId !== selectedStaff.id) {
          return false;
        }

        const appointmentStart = new Date(appointment.startDateTime);
        const appointmentEnd = new Date(appointment.endDateTime);

        if (isNaN(appointmentStart.getTime()) || isNaN(appointmentEnd.getTime())) {
          return false;
        }

        return (appointmentStart < slotEnd && appointmentEnd > slotStart);
      });

      slotAppointments.push(...appointmentItems);
    }

    return slotAppointments;
  };

  const handleTimeSlotClick = (date, timeSlot) => {
    if (view !== 'consolidated') return;

    const availabilityCheck = checkTimeSlotAvailability(date, timeSlot);
    
    if (availabilityCheck.isSalonClosed) return;

    const availabilityData = getStaffAvailabilityData(date, timeSlot);
    
    if (availabilityData) {
      setSelectedTimeSlotData({
        date,
        timeSlot,
        formattedTime: formatTime(timeSlot),
        formattedDate: formatDateHeader(date),
        ...availabilityData
      });
      setShowAvailabilityModal(true);
    }
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // #Revised UI_v2 — A5: start: multi-staff day view helpers
  const goToPreviousDay = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };

  const goToNextDay = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };

  const isSameDay = (d1, d2) =>
    d1 instanceof Date && d2 instanceof Date &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const getStaffInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  const getApptTopOffset = (startTime) => {
    const dayStartMins = calendarRange.start * 60;
    const startMins = startTime.getHours() * 60 + startTime.getMinutes();
    return Math.max(0, ((startMins - dayStartMins) / 30) * SLOT_HEIGHT);
  };

  const getApptBlockHeight = (appt) => {
    const durationMins = appt.duration || 60;
    return Math.max(SLOT_HEIGHT * 0.7, (durationMins / 30) * SLOT_HEIGHT);
  };

  const buildApptTooltip = (appt) => {
    const fmt = (d) => d instanceof Date
      ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      : '';
    return [
      appt.customerName || appt.customerEmail || 'Customer',
      appt.serviceName || '',
      `${fmt(appt.startDateTime)} – ${fmt(appt.endDateTime)}`,
      appt.customerPhone ? `📱 ${appt.customerPhone}` : '',
      appt.status === 'approved' ? '✅ Confirmed' : '⏳ Pending',
    ].filter(Boolean).join('\n');
  };

  // Staff colour palette — cycles if >5 staff
  const MS_STAFF_COLORS = [
    { bg: '#DCF5E7', text: '#166534', border: '#22C55E', avatar: '#22C55E' },
    { bg: '#EDE9FE', text: '#5B21B6', border: '#8B5CF6', avatar: '#8B5CF6' },
    { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B', avatar: '#F59E0B' },
    { bg: '#DBEAFE', text: '#1E3A8A', border: '#3B82F6', avatar: '#3B82F6' },
    { bg: '#FCE7F3', text: '#9D174D', border: '#EC4899', avatar: '#EC4899' },
  ];

  const getMsSlotClass = (staff, slot, isThisStaffOffDay, dayAppts) => {
    const bizCheck = isClosedForBusinessHours(currentDate, slot);
    const holCheck = isClosedForHolidays(currentDate);
    const ccCheck = isClosedForCustomClosures(currentDate, slot);

    if (bizCheck.isClosed || holCheck.isClosed || ccCheck.isClosed) {
      return 'ms-slot-outside-biz';
    }
    if (isThisStaffOffDay) {
      return 'ms-slot-off-day';
    }
    const leaveCheck = isStaffOnLeave(staff.id, currentDate, slot);
    if (leaveCheck.isOnLeave) {
      return 'ms-slot-leave';
    }
    const whCheck = isOutsideStaffWorkingHours(staff.id, currentDate, slot);
    if (whCheck.isOutside) {
      return 'ms-slot-outside-wh';
    }

    const slotStart = new Date(currentDate);
    slotStart.setHours(Math.floor(slot), (slot % 1) * 60, 0, 0);
    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);

    const overlappingApproved = dayAppts.filter(a =>
      a.status === 'approved' &&
      new Date(a.startDateTime) < slotEnd &&
      new Date(a.endDateTime) > slotStart
    );
    if (overlappingApproved.length > 0) {
      const overlappingServices = overlappingApproved
        .map(a => servicesById[a.serviceId])
        .filter(Boolean);
      return canShareSlot(staff, overlappingServices) ? 'ms-slot-shared' : 'ms-slot-approved';
    }

    const hasPending = dayAppts.some(a =>
      a.status === 'pending' &&
      new Date(a.startDateTime) < slotEnd &&
      new Date(a.endDateTime) > slotStart
    );
    if (hasPending) return 'ms-slot-pending';

    return 'ms-slot-available';
  };

  const renderMultiStaffDayView = () => {
    const activeStaffs = visibleStaffs.filter(s => s.isActive !== false);
    const totalHeight = timeSlots.length * SLOT_HEIGHT;

    const formatDayLabel = (d) =>
      d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    return (
      <div className="ms-day-view">
        {/* Date header */}
        <div className="ms-day-label">{formatDayLabel(currentDate)}</div>

        {activeStaffs.length === 0 ? (
          <div className="ms-empty">No active staff found. Add staff members in the Core Module.</div>
        ) : (
          <div className="ms-grid-outer">
            {/* Sticky column header row */}
            <div className="ms-header-row" style={{ gridTemplateColumns: `60px repeat(${activeStaffs.length}, 1fr)` }}>
              <div className="ms-corner" />
              {activeStaffs.map((staff, idx) => {
                const color = MS_STAFF_COLORS[idx % MS_STAFF_COLORS.length];
                return (
                  <div key={staff.id} className="ms-staff-col-header">
                    <div className="ms-staff-avatar" style={{ background: color.avatar }}>
                      {getStaffInitials(staff.name)}
                    </div>
                    <span className="ms-staff-name">{staff.name?.split(' ')[0] || staff.name}</span>
                  </div>
                );
              })}
            </div>

            {/* Scrollable body */}
            <div className="ms-body-scroll">
              <div className="ms-body-inner" style={{ gridTemplateColumns: `60px repeat(${activeStaffs.length}, 1fr)` }}>
                {/* Time labels */}
                <div className="ms-time-col">
                  {timeSlots.map(slot => (
                    <div key={slot} className="ms-time-cell" style={{ height: SLOT_HEIGHT }}>
                      {formatTime(slot)}
                    </div>
                  ))}
                </div>

                {/* Staff columns */}
                {activeStaffs.map((staff, idx) => {
                  const color = MS_STAFF_COLORS[idx % MS_STAFF_COLORS.length];
                  const dayAppts = visibleAppointments.filter(a =>
                    a.staffId === staff.id &&
                    (a.status === 'approved' || a.status === 'pending') &&
                    isSameDay(a.startDateTime, currentDate)
                  );

                  const staffOffDay = workingHoursData[staff.id]?.offDay;
                  const offDayList = staffOffDay ? (Array.isArray(staffOffDay) ? staffOffDay : [staffOffDay]) : [];
                  const isThisStaffOffDay = offDayList.includes(DAY_NAMES[currentDate.getDay()]);

                  return (
                    <div key={staff.id} className="ms-staff-col" style={{ height: totalHeight, position: 'relative' }}>
                      {/* Slot background rows — coloured by status */}
                      {timeSlots.map(slot => (
                        <div
                          key={slot}
                          className={`ms-grid-row ${getMsSlotClass(staff, slot, isThisStaffOffDay, dayAppts)}`}
                          style={{ height: SLOT_HEIGHT }}
                        />
                      ))}
                      {/* Off-day label overlay */}
                      {isThisStaffOffDay && (
                        <div className="ms-off-day-overlay">
                          <div className="ms-off-day-label">
                            {staff.name?.split(' ')[0] || staff.name} — Off Day
                          </div>
                        </div>
                      )}
                      {/* Appointment blocks */}
                      {dayAppts.map(appt => (
                        <div
                          key={appt.id}
                          className={`ms-appt-block${appt.status === 'pending' ? ' pending' : ''}`}
                          style={{
                            top: getApptTopOffset(appt.startDateTime),
                            height: getApptBlockHeight(appt),
                            background: color.bg,
                            color: color.text,
                            borderLeft: `3px solid ${color.border}`,
                          }}
                          data-tooltip={buildApptTooltip(appt)}
                          onClick={() => { setSelectedAppointment(appt); setShowAppointmentModal(true); }}
                          title={buildApptTooltip(appt)}
                        >
                          <div className="ms-appt-name">
                            {appt.customerName || appt.customerEmail || 'Customer'}
                          </div>
                          <div className="ms-appt-svc">{appt.serviceName}</div>
                          <div className={`ms-appt-dot ${appt.status}`} />
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  // #Revised UI_v2 — A5: end

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formatTime = (timeSlot) => {
    const hour = Math.floor(timeSlot);
    const minutes = (timeSlot % 1) * 60;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const displayMinutes = minutes === 0 ? '00' : minutes.toString().padStart(2, '0');
    return `${displayHour}:${displayMinutes} ${period}`;
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time24;
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatDateHeader = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatRequestDateTime = (dateTime) => {
    if (!dateTime) return '';
    return dateTime.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderLegend = () => {
    if (view === 'consolidated') {
      return (
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-color all-staffs-available"></div>
            <span>All Staffs Available</span>
          </div>
          <div className="legend-item">
            <div className="legend-color some-staffs-available"></div>
            <span>Some Staffs Available</span>
          </div>
          <div className="legend-item">
            <div className="legend-color all-staffs-occupied"></div>
            <span>All Staffs Occupied</span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-color available"></div>
            <span>Available Time</span>
          </div>
          <div className="legend-item">
            <div className="legend-color pending"></div>
            <span>Pending Request</span>
          </div>
          <div className="legend-item">
            <div className="legend-color approved"></div>
            <span>Approved Booking</span>
          </div>
          <div className="legend-item">
            <div className="legend-color shared"></div>
            <span>Shared Slot — Room for More</span>
          </div>
          <div className="legend-item">
            <div className="legend-color unavailable"></div>
            <span>Outside Business Hours</span>
          </div>
          <div className="legend-item">
            <div className="legend-color leave"></div>
            <span>Staff Leave</span>
          </div>
          <div className="legend-item">
            <div className="legend-color outside-working-hours"></div>
            <span>Outside Staff Working Hours</span>
          </div>
          <div className="legend-item">
            <div className="legend-color off-day"></div>
            <span>Staff Off Day</span>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="booking-calendar-loading">
        <div className="spinner"></div>
        <p>Loading booking calendar...</p>
      </div>
    );
  }

// CONTINUE TO PART 2 - DO NOT CLOSE THE FUNCTION YET

// PART 2 - Continue from Part 1 (this is still inside the BookingCalendarTab function)

  return (
    <div className="booking-calendar-container">
      <div className="editor-page-header" style={{ padding: '16px 20px 0', marginBottom: '0' }}>
        <h2>Calendar Management</h2>
        <div className="editor-page-actions">
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>
      {/* Compact Booking Statistics bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        padding: '10px 16px',
        backgroundColor: '#fff',
        border: '1px solid var(--light-coral)',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(230,76,49,0.07)',
        marginBottom: '10px'
      }}>
        <span style={{
          fontFamily: "'Familjen Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: '13px',
          color: 'var(--primary-coral)',
          whiteSpace: 'nowrap',
          marginRight: '4px'
        }}>
          Booking Statistics
        </span>

        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 12px',
          backgroundColor: '#fff0ee',
          border: '1px solid var(--light-coral)',
          borderRadius: '20px',
          fontSize: '13px',
          fontFamily: "'Wix Madefor Text', sans-serif",
          color: '#c0392b',
          whiteSpace: 'nowrap'
        }}>
          Pending&nbsp;<strong style={{ fontFamily: "'Familjen Grotesk', sans-serif", fontSize: '14px' }}>{getFilteredSummaryCount('pending')}</strong>
        </span>

        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 12px',
          backgroundColor: '#edfaf3',
          border: '1px solid #a8e6c3',
          borderRadius: '20px',
          fontSize: '13px',
          fontFamily: "'Wix Madefor Text', sans-serif",
          color: '#1a7a4a',
          whiteSpace: 'nowrap'
        }}>
          Approved&nbsp;<strong style={{ fontFamily: "'Familjen Grotesk', sans-serif", fontSize: '14px' }}>{getFilteredSummaryCount('approved')}</strong>
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{
            fontSize: '12px',
            fontFamily: "'Familjen Grotesk', sans-serif",
            fontWeight: 600,
            color: '#888',
            whiteSpace: 'nowrap'
          }}>Period:</label>
          <select
            value={summaryTimeFilter}
            onChange={(e) => setSummaryTimeFilter(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1.5px solid var(--light-coral)',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: "'Wix Madefor Text', sans-serif",
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="calendar-header">

        {/* Mandatory location selector — shown only for multi-location salons */}
        {isMultiLocation && (
          <div className="booking-location-bar">
            <span className="booking-location-bar-label">Location:</span>
            {locations.map(loc => (
              <button
                key={loc.locationId}
                className={`booking-loc-pill${locationId === loc.locationId ? ' booking-loc-pill--active' : ''}`}
                onClick={() => onLocationChange && onLocationChange(loc.locationId)}
              >
                {loc.locationName}
              </button>
            ))}
          </div>
        )}

        {/* #Revised UI_v2 — A5: start: updated calendar controls with multi-staff day view */}
        <div className="calendar-controls">
          <div className="view-selector">
            <button
              className={view === 'consolidated' ? 'active' : ''}
              onClick={() => setView('consolidated')}
            >
              Consolidated View
            </button>
            <button
              className={view === 'individual' ? 'active' : ''}
              onClick={() => setView('individual')}
            >
              Individual Staff View
            </button>
            <button
              className={view === 'multistaff' ? 'active' : ''}
              onClick={() => setView('multistaff')}
            >
              Multi-Staff Day View
            </button>
          </div>

          {view === 'individual' && (
            <div className="staff-selector">
              <select
                value={selectedStaff?.id || ''}
                onChange={(e) => {
                  const staff = staffs.find(s => s.id === e.target.value);
                  setSelectedStaff(staff);
                }}
              >
                <option value="">Select Staff</option>
                {visibleStaffs
                  .filter(s => s.isActive !== false)
                  .map(staff => (
                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                  ))}
              </select>
            </div>
          )}

          <div className="date-navigation">
            {view === 'multistaff' ? (
              <>
                <button className="today-btn" onClick={goToToday}>Today</button>
                <button onClick={goToPreviousDay}>‹ Prev Day</button>
                <button onClick={goToNextDay}>Next Day ›</button>
              </>
            ) : (
              <>
                <button onClick={goToPreviousWeek}>← Previous Week</button>
                <button onClick={goToToday}>Today</button>
                <button onClick={goToNextWeek}>Next Week →</button>
              </>
            )}
          </div>
        </div>
        {/* #Revised UI_v2 — A5: end */}
      </div>

      {/* View mode note */}
      <p style={{
        margin: '8px 0 0 0',
        fontSize: '12px',
        fontFamily: "'Wix Madefor Text', sans-serif",
        color: '#888',
        lineHeight: '1.5'
      }}>
        Note: "Consolidated View" will show only approved appointments while "Individual Staff View" will show both pending and approved appointments.
      </p>

      {/* Dynamic Legend */}
      {renderLegend()}

      {/* #Revised UI_v2 — A5: multi-staff day view */}
      {view === 'multistaff' && renderMultiStaffDayView()}

      {/* Week/Individual calendar grid — hidden when multi-staff day view is active */}
      {view !== 'multistaff' && <div className="calendar-grid-wrapper">
        {/* Sticky Header Row */}
        <div className="calendar-header-row">
          <div className="time-header" style={{ height: `${SLOT_HEIGHT}px` }}>Time</div>
          {weekDates.map(date => (
            <div
              key={`header-${date.toISOString()}`}
              className="day-header"
              style={{ height: `${SLOT_HEIGHT}px` }}
            >
              {formatDateHeader(date)}
            </div>
          ))}
        </div>

        {/* Scrollable Calendar Grid */}
        <div className="calendar-grid">
          <div className="time-column">
            {timeSlots.map(timeSlot => (
              <div
                key={timeSlot}
                className="time-slot-label"
                style={{
                  height: `${SLOT_HEIGHT}px`,
                  lineHeight: `${SLOT_HEIGHT}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {formatTime(timeSlot)}
              </div>
            ))}
          </div>

          {weekDates.map(date => (
            <div key={date.toISOString()} className="day-column">
              {timeSlots.map(timeSlot => {
              // Show consolidated view if: view is 'consolidated' OR (view is 'individual' but no staff selected)
              const showConsolidatedView = view === 'consolidated' || (view === 'individual' && !selectedStaff);

              const availabilityCheck = checkTimeSlotAvailability(
                date,
                timeSlot,
                (view === 'individual' && selectedStaff) ? selectedStaff.id : null
              );

              const { isSalonClosed, salonReasons, hasStaffIssues, staffReasons, outsideWorkingHours, isOffDay, isAvailable, isSharedSlot } = availabilityCheck;

              let timeSlotClass = 'time-slot';
              const availabilityData = showConsolidatedView ? getStaffAvailabilityData(date, timeSlot) : null;

              if (showConsolidatedView && availabilityData && !isSalonClosed) {
                if (availabilityData.availableCount === availabilityData.totalStaffs && availabilityData.totalStaffs > 0) {
                  timeSlotClass += ' all-staffs-available';
                } else if (availabilityData.availableCount > 0) {
                  timeSlotClass += ' some-staffs-available';
                } else {
                  timeSlotClass += ' all-staffs-occupied';
                }
                timeSlotClass += ' clickable-slot';
              } else if (view === 'individual' && selectedStaff) {
                if (isSalonClosed) {
                  timeSlotClass += ' closed';
                } else if (hasStaffIssues) {
                  const slotStart = new Date(date);
                  const hour = Math.floor(timeSlot);
                  const minutes = (timeSlot % 1) * 60;
                  slotStart.setHours(hour, minutes, 0, 0);

                  const slotEnd = new Date(slotStart);
                  slotEnd.setMinutes(slotEnd.getMinutes() + 30);

                  const hasApprovedAppointment = selectedStaff && visibleAppointments.some(appointment => {
                    if (appointment.staffId !== selectedStaff.id || appointment.status !== 'approved') return false;

                    const appointmentStart = new Date(appointment.startDateTime);
                    const appointmentEnd = new Date(appointment.endDateTime);

                    return (appointmentStart < slotEnd && appointmentEnd > slotStart);
                  });

                  if (hasApprovedAppointment) {
                    timeSlotClass += ' approved';
                  } else {
                    timeSlotClass += ' unavailable';
                  }
                } else if (outsideWorkingHours) {
                  timeSlotClass += isOffDay ? ' staff-off-day' : ' outside-working-hours';
                } else {
                  timeSlotClass += isSharedSlot ? ' available shared' : ' available';
                }
              } else {
                timeSlotClass += isAvailable ? ' available' : ' closed';
              }
              
              const slotAppointments = (view === 'individual' && selectedStaff) ? getAppointmentsForSlot(date, timeSlot) : [];

              let individualStaffLeave = null;
              if (view === 'individual' && selectedStaff && !isSalonClosed) {
                const leaveCheck = isStaffOnLeave(selectedStaff.id, date, timeSlot);
                if (leaveCheck.isOnLeave) {
                  individualStaffLeave = leaveCheck;
                  // Add purple background for leave
                  timeSlotClass += ' staff-on-leave';
                }
              }
              
              return (
                <div 
                  key={`${date.toISOString()}-${timeSlot}`}
                  className={timeSlotClass}
                  style={{
                    height: `${SLOT_HEIGHT}px`,
                    cursor: showConsolidatedView && availabilityData && !isSalonClosed ? 'pointer' : 'default'
                  }}
                  onClick={() => showConsolidatedView && !isSalonClosed ? handleTimeSlotClick(date, timeSlot) : null}
                >
                  {isSalonClosed && (
                    <div className="unavailability-overlay salon-level">
                      <div className="unavailability-text">
                        {salonReasons[0]}
                      </div>
                    </div>
                  )}
                  
                  {showConsolidatedView && !isSalonClosed && availabilityData && (
                    <div className="staff-availability-summary">
                      <div className="availability-count">
                        {availabilityData.occupiedCount} of {availabilityData.totalStaffs} Staffs Occupied
                      </div>
                      <div className="availability-detail">
                        Available: {availabilityData.availableCount} | Occupied: {availabilityData.occupiedCount}
                      </div>
                    </div>
                  )}
                  
                  {view === 'individual' && selectedStaff && !isSalonClosed && individualStaffLeave && (
                    <div
                      className="unavailability-overlay staff-individual clickable"
                      onClick={() => {
                        setSelectedLeave(individualStaffLeave.leaveRecord);
                        setShowLeaveModal(true);
                      }}
                    >
                      <div className="unavailability-text">
                        {individualStaffLeave.reason}
                      </div>
                    </div>
                  )}

                  {view === 'individual' && selectedStaff && !isSalonClosed && isOffDay && (
                    <div className="unavailability-overlay staff-off-day">
                      <div className="unavailability-text">
                        {selectedStaff.name} — Off Day
                      </div>
                    </div>
                  )}

                  {view === 'individual' && Array.isArray(slotAppointments) && slotAppointments.map(appointment => (
                    <div
                      key={appointment.id}
                      className={`booking-item ${appointment.status || 'pending'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAppointment(appointment);
                        setShowAppointmentModal(true);
                      }}
                    >
                      <div className="booking-service">{appointment.serviceName || 'Service'}</div>
                      <div className="booking-customer">
                        {appointment.customerName || appointment.customerEmail || 'Customer'}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
        </div>
      </div>}




      {/* ALL MODALS */}
      
      {/* Staff Availability Modal */}
      {showAvailabilityModal && selectedTimeSlotData && (
        <div className="modal-overlay" onClick={() => setShowAvailabilityModal(false)}>
          <div className="booking-request-modal staff-availability-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Staff Availability - {selectedTimeSlotData.formattedDate} at {selectedTimeSlotData.formattedTime}</h3>
              <button 
                className="close-button"
                onClick={() => setShowAvailabilityModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="availability-summary">
                <div className="summary-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Staffs:</span>
                    <span className="stat-value">{selectedTimeSlotData.totalStaffs}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Available:</span>
                    <span className="stat-value available">{selectedTimeSlotData.availableCount}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Occupied:</span>
                    <span className="stat-value occupied">{selectedTimeSlotData.occupiedCount}</span>
                  </div>
                </div>
              </div>

              {/* Current Bookings Section - Show prominently above Available Staffs */}
              {selectedTimeSlotData.currentBookings && selectedTimeSlotData.currentBookings.length > 0 && (
                <div className="staffs-section current-bookings-section">
                  <h4>Current Bookings</h4>
                  <div className="current-bookings-list">
                    {selectedTimeSlotData.currentBookings.map((booking, index) => (
                      <div key={`${booking.staffId}-${index}`} className="current-booking-item">
                        <div className="booking-staff-name">{booking.staffName}</div>
                        <div className="booking-customer-name">{booking.customerName}</div>
                        <div className="booking-service-info">{booking.serviceName} ({booking.duration}min)</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTimeSlotData.availableStaffs.length > 0 && (
                <div className="staffs-section">
                  <h4>Available Staffs</h4>
                  <div className="staffs-list">
                    {selectedTimeSlotData.availableStaffs.map(staff => (
                      <div key={staff.id} className="staff-card available-staff">
                        <div className="staff-name">
                          {staff.name}
                          {staff.sharedBookings && staff.sharedBookings.length > 0 && (
                            <span className="shared-slot-badge" title="Slot has an existing booking but still has room for a compatible service">
                              🔗 Shared slot
                            </span>
                          )}
                        </div>
                        <div className="staff-services">
                          <strong>Services:</strong>
                          {staff.services.length > 0 ? (
                            <ul className="services-list">
                              {staff.services.map(service => (
                                <li key={service.id}>{service.name}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="no-services">No services assigned</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTimeSlotData.occupiedStaffs.length > 0 && (
                <div className="staffs-section">
                  <h4>Occupied Staffs</h4>
                  <div className="staffs-list">
                    {selectedTimeSlotData.occupiedStaffs.map(staff => (
                      <div key={staff.id} className="staff-card occupied-staff">
                        <div className="staff-name">{staff.name}</div>
                        {staff.type === 'leave' ? (
                          <div className="staff-status">
                            <span className="status-type">On Leave</span>
                            <span className="status-reason">{staff.reason}</span>
                          </div>
                        ) : staff.type === 'booked' && staff.bookings ? (
                          <div className="staff-status">
                            <span className="status-type">Booked</span>
                            <div className="bookings-info">
                              {staff.bookings.map((booking, index) => (
                                <div key={index} className="booking-info">
                                  <span className="customer">{booking.customerName}</span>
                                  <span className="service">{booking.serviceName} ({booking.duration}min)</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="staff-status">
                            <span className="status-type">{staff.reason || 'Unavailable'}</span>
                          </div>
                        )}
                        <div className="staff-services">
                          <strong>Services:</strong>
                          {staff.services.length > 0 ? (
                            <ul className="services-list">
                              {staff.services.map(service => (
                                <li key={service.id}>{service.name}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="no-services">No services assigned</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTimeSlotData.totalStaffs === 0 && (
                <div className="no-staffs-message">
                  <p>No active staffs found for this salon.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Request Modal */}
      {showRequestModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="booking-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Request Details</h3>
              <button 
                className="close-button"
                onClick={() => setShowRequestModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="request-details">
                <div className="detail-row">
                  <label>Customer:</label>
                  <span>{selectedRequest.customerName || selectedRequest.customerEmail}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{selectedRequest.customerEmail}</span>
                </div>
                <div className="detail-row">
                  <label>Staff:</label>
                  <span>{selectedRequest.staffName}</span>
                </div>
                <div className="detail-row">
                  <label>Service:</label>
                  <span>{selectedRequest.serviceName}</span>
                </div>
                <div className="detail-row">
                  <label>Duration:</label>
                  <span>{selectedRequest.duration} minutes</span>
                </div>
                <div className="detail-row">
                  <label>Price:</label>
                  <span>${selectedRequest.price || 'TBD'}</span>
                </div>
                <div className="detail-row">
                  <label>Appointment Date:</label>
                  <span>{selectedRequest.startDateTime?.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>Appointment Time:</label>
                  <span>{selectedRequest.startDateTime?.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>End Time:</label>
                  <span>{selectedRequest.endDateTime?.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>Date Created:</label>
                  <span>{selectedRequest.dateTimeCreated?.toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status ${selectedRequest.status}`}>
                    {selectedRequest.status.toUpperCase()}
                  </span>
                </div>
                {selectedRequest.notes && (
                  <div className="detail-row">
                    <label>Notes:</label>
                    <span>{selectedRequest.notes}</span>
                  </div>
                )}
              </div>
              
              {selectedRequest.status === 'pending' && (
                <div className="modal-actions">
                  <button 
                    className="approve-button"
                    onClick={() => handleAppointmentStatusChange(selectedRequest.id, 'approved')}
                  >
                    Approve Request
                  </button>
                  <button 
                    className="reject-button"
                    onClick={() => handleAppointmentStatusChange(selectedRequest.id, 'rejected')}
                  >
                    Reject Request
                  </button>
                </div>
              )}
              
              {selectedRequest.status === 'approved' && (
                <div className="modal-actions">
                  <button 
                    className="cancel-button"
                    onClick={() => openCancelReason(selectedRequest)}
                  >
                    Cancel Booking
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      {showAppointmentModal && selectedAppointment && (
        <div className="modal-overlay" onClick={() => setShowAppointmentModal(false)}>
          <div className="booking-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Details</h3>
              <button 
                className="close-button"
                onClick={() => setShowAppointmentModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="request-details">
                <div className="detail-row">
                  <label>Appointment ID:</label>
                  <span>{selectedAppointment.id}</span>
                </div>
                <div className="detail-row">
                  <label>Customer:</label>
                  <span>{selectedAppointment.customerName || selectedAppointment.customerEmail}</span>
                </div>
                <div className="detail-row">
                  <label>Staff:</label>
                  <span>{selectedAppointment.staffName}</span>
                </div>
                <div className="detail-row">
                  <label>Service:</label>
                  <span>{selectedAppointment.serviceName}</span>
                </div>
                <div className="detail-row">
                  <label>Duration:</label>
                  <span>{selectedAppointment.duration} minutes</span>
                </div>
                <div className="detail-row">
                  <label>Price:</label>
                  <span>${selectedAppointment.price || 'TBD'}</span>
                </div>
                <div className="detail-row">
                  <label>Appointment Date:</label>
                  <span>{selectedAppointment.startDateTime?.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>Appointment Time:</label>
                  <span>{selectedAppointment.startDateTime?.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>End Time:</label>
                  <span>{selectedAppointment.endDateTime?.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status ${selectedAppointment.status}`}>
                    {selectedAppointment.status.toUpperCase()}
                  </span>
                </div>
                {selectedAppointment.notes && (
                  <div className="detail-row">
                    <label>Notes:</label>
                    <span>{selectedAppointment.notes}</span>
                  </div>
                )}
              </div>
              
              {selectedAppointment.status === 'approved' && (
                <div className="modal-actions">
                  <button 
                    className="cancel-button"
                    onClick={() => openCancelReason(selectedAppointment)}
                  >
                    Cancel Appointment
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leave Modal */}
      {/* Cancel reason modal (opened by Cancel Booking / Cancel Appointment) */}
      {showCancelReasonModal && cancelReasonTarget && (
        <div className="modal-overlay" onClick={() => setShowCancelReasonModal(false)}>
          <div className="booking-request-modal cancel-reason-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Appointment</h3>
              <button className="close-button" onClick={() => setShowCancelReasonModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <p className="cancel-reason-intro">
                You are cancelling the appointment for <strong>{cancelReasonTarget.customerName || cancelReasonTarget.customerEmail}</strong>
                {cancelReasonTarget.startDateTime ? <> on <strong>{new Date(cancelReasonTarget.startDateTime).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</strong></> : null}.
              </p>
              <p className="cancel-reason-intro">Please provide a reason — this will be shown to the customer in their portal.</p>
              <textarea
                className="cancel-reason-textarea"
                placeholder="Enter cancellation reason..."
                value={cancelReasonText}
                onChange={e => setCancelReasonText(e.target.value)}
                rows={4}
                autoFocus
              />
              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={handleCancelWithReason}
                  disabled={!cancelReasonText.trim()}
                >
                  Confirm Cancellation
                </button>
                <button
                  className="approve-button"
                  style={{ backgroundColor: '#666', borderColor: '#666' }}
                  onClick={() => setShowCancelReasonModal(false)}
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLeaveModal && selectedLeave && (
        <div className="modal-overlay" onClick={() => setShowLeaveModal(false)}>
          <div className="booking-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Leave Details</h3>
              <button 
                className="close-button"
                onClick={() => setShowLeaveModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="request-details">
                <div className="detail-row">
                  <label>Staff:</label>
                  <span>{selectedLeave.staffName}</span>
                </div>
                <div className="detail-row">
                  <label>Leave Type:</label>
                  <span className={`leave-type ${selectedLeave.type}`}>
                    {selectedLeave.type === 'annual' ? 'Annual Leave' : 
                     selectedLeave.type === 'medical' ? 'Medical Leave' : 
                     selectedLeave.type === 'timeoff' ? 'Time Off' : 
                     selectedLeave.type || 'Leave'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Time Type:</label>
                  <span>
                    {selectedLeave.timeType === 'full' ? 'Full Day' :
                     selectedLeave.timeType === 'am' ? 'Morning (AM)' :
                     selectedLeave.timeType === 'pm' ? 'Afternoon (PM)' :
                     selectedLeave.timeType === 'custom' ? 'Custom Time' :
                     selectedLeave.timeType || 'Full Day'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Start Date:</label>
                  <span>{selectedLeave.startDate?.toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <label>End Date:</label>
                  <span>{selectedLeave.endDate?.toLocaleDateString()}</span>
                </div>
                {selectedLeave.timeType === 'custom' && selectedLeave.startTime && (
                  <>
                    <div className="detail-row">
                      <label>Start Time:</label>
                      <span>{formatTime12Hour(selectedLeave.startTime)}</span>
                    </div>
                    <div className="detail-row">
                      <label>End Time:</label>
                      <span>{formatTime12Hour(selectedLeave.endTime)}</span>
                    </div>
                  </>
                )}
                <div className="detail-row">
                  <label>Reason:</label>
                  <span>{selectedLeave.reason || 'No reason provided'}</span>
                </div>
                <div className="detail-row">
                  <label>Notes:</label>
                  <span>{selectedLeave.notes || 'No additional notes'}</span>
                </div>
                <div className="detail-row">
                  <label>Created By:</label>
                  <span>{selectedLeave.createdBy}</span>
                </div>
                <div className="detail-row">
                  <label>Created At:</label>
                  <span>{selectedLeave.createdAt?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingCalendarTab;